import { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Mail, Sparkles, AlertTriangle,
  Loader2, Upload, Send, RefreshCw
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLAG_COLORS = {
  green: 'bg-green-50 text-green-700 border-green-200',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  gray: 'bg-slate-50 text-slate-500 border-slate-200',
};

function Cell({ value, flag, fmt = v => v }) {
  if (value === null || value === undefined || value === '') {
    return <td className="px-2 py-2 text-center text-slate-300 text-xs">—</td>;
  }
  const cls = FLAG_COLORS[flag] || FLAG_COLORS.gray;
  return (
    <td className={`px-2 py-2 text-center text-xs font-semibold border-r border-slate-100 ${cls}`}>
      {fmt(value)}
    </td>
  );
}

export default function WeeklyAnalysis() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all'); // 'all' | 'mine'
  const fileInputRef = useState({ current: null })[0];

  const isStoreUser = !!user?.store_id && ['manager', 'employee', 'supervisor'].includes(user?.role);
  useEffect(() => {
    if (isStoreUser) setScopeFilter('mine');
  }, [isStoreUser]);

  const importExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/weekly-analysis/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${res.data.inserted + res.data.updated} rows across ${res.data.weeks_count} weeks!`);
      await fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Excel import failed');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const fetch = async (week = null) => {
    setLoading(true);
    try {
      const params = week ? `?week_ending=${week}` : '';
      const res = await axios.get(`${API_URL}/weekly-analysis${params}`);
      setData(res.data);
      setSelectedWeek(res.data.week_ending || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load weekly analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const generateFeedback = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/weekly-analysis/generate-feedback?week_ending=${selectedWeek}`);
      toast.success(`Rooty generated feedback for ${res.data.generated} stores`);
      await fetch(selectedWeek);
    } catch (e) {
      toast.error('Failed to generate AI feedback');
    } finally {
      setGenerating(false);
    }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const res = await axios.post(`${API_URL}/weekly-analysis/email`, {
        week_ending: selectedWeek,
        include_ai_feedback: true,
      });
      toast.success(`Weekly report emailed to ${res.data.recipients.length} recipients`);
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>;
  }

  if (!data || !data.stores?.length) {
    return (
      <div className="space-y-5">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Weekly Analysis</h1>
              <p className="text-white/80 text-sm mt-1">Store-by-store performance vs A&W targets</p>
            </div>
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={importExcel}
              className="hidden"
              id="excel-import-input-empty"
              data-testid="excel-import-input-empty"
            />
            <label htmlFor="excel-import-input-empty">
              <Button
                asChild
                disabled={importing}
                className="bg-white text-orange-600 hover:bg-orange-50 rounded-lg font-semibold cursor-pointer"
              >
                <span>
                  {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import Weekly Excel
                </span>
              </Button>
            </label>
          </div>
        </div>
        <Card className="p-12 text-center">
          <p className="text-slate-500 mb-2">No KPI data yet.</p>
          <p className="text-sm text-slate-400">Click "Import Weekly Excel" above to upload your Elmwood weekly analysis spreadsheet, or import a CSV from the KPI Dashboard.</p>
        </Card>
      </div>
    );
  }

  const t = data.totals;
  const growthColor = t.sales_growth >= 0 ? 'text-green-600' : 'text-red-600';
  const GrowthIcon = t.sales_growth >= 0 ? TrendingUp : TrendingDown;

  // Apply scope filter: "mine" filters to user's store, "all" shows everyone
  const visibleStores = scopeFilter === 'mine' && user?.store_id
    ? data.stores.filter(s => s.store_id === user.store_id)
    : data.stores;

  return (
    <div className="space-y-5" data-testid="weekly-analysis-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Weekly Analysis</h1>
            <p className="text-white/80 text-sm mt-1">Week ending {data.week_ending}{data.previous_week && ` · vs ${data.previous_week}`}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={importExcel}
              className="hidden"
              id="excel-import-input"
              data-testid="excel-import-input"
            />
            <label htmlFor="excel-import-input">
              <Button
                asChild
                disabled={importing}
                className="bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-lg cursor-pointer"
              >
                <span data-testid="import-excel-btn">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Import Excel
                </span>
              </Button>
            </label>
            <select
              value={selectedWeek}
              onChange={(e) => fetch(e.target.value)}
              className="bg-white/15 text-white text-sm px-3 py-2 rounded-lg border border-white/30 focus:outline-none"
              data-testid="week-selector"
            >
              {(data.available_weeks || []).map(w => (
                <option key={w} value={w} className="text-slate-900">{w}</option>
              ))}
            </select>
            <Button
              onClick={generateFeedback}
              disabled={generating}
              className="bg-white/15 hover:bg-white/25 text-white border border-white/30 rounded-lg"
              data-testid="generate-feedback-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate Rooty Feedback
            </Button>
            <Button
              onClick={emailReport}
              disabled={emailing}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-lg font-semibold"
              data-testid="email-report-btn"
            >
              {emailing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Email Report
            </Button>
          </div>
        </div>
      </div>

      {/* Scope toggle — only useful when user has a home store */}
      {user?.store_id && (
        <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-slate-200 w-fit text-xs font-medium" data-testid="scope-toggle">
          <button
            onClick={() => setScopeFilter('mine')}
            className={`px-4 py-1.5 rounded-md transition-colors ${scopeFilter === 'mine' ? 'bg-orange-500 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="scope-mine"
          >My Store</button>
          <button
            onClick={() => setScopeFilter('all')}
            className={`px-4 py-1.5 rounded-md transition-colors ${scopeFilter === 'all' ? 'bg-orange-500 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="scope-all"
          >All Stores</button>
        </div>
      )}

      {/* Company Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Total Sales</p>
          <p className="text-xl font-bold text-slate-900 mt-1">${t.total_sales.toLocaleString()}</p>
          <p className={`text-xs font-semibold mt-1 flex items-center gap-1 ${growthColor}`}>
            <GrowthIcon className="w-3 h-3" /> {t.sales_growth >= 0 ? '+' : ''}{t.sales_growth}% vs LY
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Avg Labour</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{t.avg_labor}%</p>
          <p className="text-xs text-slate-400 mt-1">Target 19%</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Avg Food Cost</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{t.avg_food_cost}%</p>
          <p className="text-xs text-slate-400 mt-1">Target 32%</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Avg DT Time</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{t.avg_dt_time}s</p>
          <p className="text-xs text-slate-400 mt-1">Target 80s</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Complaints</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{t.total_complaints}</p>
          <p className="text-xs text-slate-400 mt-1">{t.store_count} stores</p>
        </Card>
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <Card className="p-4 border-red-200 bg-red-50/30" data-testid="alerts-card">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <h3 className="font-bold text-sm text-red-900">{data.alerts.length} item{data.alerts.length !== 1 && 's'} needing attention</h3>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.alerts.map((a, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                {a.store}: {a.metric}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Store-by-Store Grid */}
      <Card className="overflow-x-auto" data-testid="weekly-grid">
        <table className="w-full text-xs">
          <thead className="bg-slate-900 text-white">
            <tr>
              <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-slate-900 z-10">Store</th>
              <th className="px-2 py-2">Net Sales</th>
              <th className="px-2 py-2">vs LY</th>
              <th className="px-2 py-2">Disc%</th>
              <th className="px-2 py-2">Labour%</th>
              <th className="px-2 py-2">Food%</th>
              <th className="px-2 py-2">D/T</th>
              <th className="px-2 py-2">Inside</th>
              <th className="px-2 py-2">Cash</th>
              <th className="px-2 py-2">Inv</th>
              <th className="px-2 py-2">Medallia</th>
              <th className="px-2 py-2">Google</th>
              <th className="px-2 py-2">Compl.</th>
            </tr>
          </thead>
          <tbody>
            {data.stores.map(s => visibleStores.includes(s) ? (
              <Fragment key={s.store_id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold text-slate-800 sticky left-0 bg-white border-r border-slate-100 whitespace-nowrap">
                    {s.store_name}
                  </td>
                  <Cell value={s.net_sales} flag="gray" fmt={v => `$${v.toLocaleString(undefined, {maximumFractionDigits: 0})}`} />
                  <Cell value={s.sales_growth} flag={s.sales_growth >= 0 ? 'green' : 'red'} fmt={v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`} />
                  <Cell value={s.discount_percent} flag={s.discount_flag} fmt={v => `${v.toFixed(1)}%`} />
                  <Cell value={s.labor_percent} flag={s.labor_flag} fmt={v => `${v.toFixed(1)}%`} />
                  <Cell value={s.total_food_cost} flag={s.food_flag} fmt={v => `${v.toFixed(1)}%`} />
                  <Cell value={s.service_time_dt} flag={s.dt_flag} fmt={v => `${Math.round(v)}s`} />
                  <Cell value={s.service_time_inside} flag={s.inside_flag} fmt={v => `${Math.round(v)}s`} />
                  <Cell value={s.cash_variance} flag={s.cash_flag} fmt={v => `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`} />
                  <Cell value={s.inventory_variance} flag={s.inv_flag} fmt={v => `${v.toFixed(2)}%`} />
                  <Cell value={s.medallia_score} flag={s.mystery_flag} fmt={v => v.toFixed(0)} />
                  <Cell value={s.google_score} flag="gray" fmt={v => v.toFixed(0)} />
                  <Cell value={s.guest_complaints} flag="gray" fmt={v => v} />
                </tr>
                {s.feedback && (
                  <tr className="bg-orange-50/40">
                    <td colSpan={13} className="px-4 py-2 text-xs text-slate-600 italic border-t border-orange-100">
                      <Sparkles className="w-3 h-3 inline mr-1 text-orange-500" />
                      <strong className="text-slate-800 not-italic">Rooty:</strong> {s.feedback}
                    </td>
                  </tr>
                )}
              </Fragment>
            ) : null)}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-slate-400 text-center pb-4">
        Color legend: <span className="text-green-600 font-semibold">Green</span> = on target ·
        <span className="text-amber-600 font-semibold"> Amber</span> = warning ·
        <span className="text-red-600 font-semibold"> Red</span> = needs attention
      </p>
    </div>
  );
}
